import * as React from 'react';
import {
  Select as SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Select as Interface } from '../../design/components';

export const Select: React.FC<Interface> = ({
  name, description, state, placeholder, hidden, inline, readonly, disabled, required,
  error, options = [], clearable
}) => {
  const [value, setValue] = state || ['', () => {}];

  // Inline mode: render the selected option's label or value as text
  if (inline) {
    const selected = options.find(opt => opt.state?.[0] === value);
    return (
      <span className={cn(hidden && 'hidden')}>
        {selected?.label || value || ''}
      </span>
    );
  }

  return (
    <div className={cn('flex flex-col gap-2', hidden && 'hidden')}>
      {name && (
        <Label htmlFor={name} className={cn(error && 'text-destructive')}>
          {name}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <SelectRoot
        value={value}
        onValueChange={(v) => setValue(v === '__clear__' ? '' : v)}
        disabled={disabled || readonly}
        required={required}
      >
        <SelectTrigger
          id={name}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || description ? `${name}-description` : undefined}
          className={cn(readonly && 'bg-muted cursor-default')}
        >
          <SelectValue placeholder={placeholder || name} />
        </SelectTrigger>
        <SelectContent>
          {clearable && (
            <SelectItem value="__clear__">
              <em className="text-muted-foreground">{placeholder || 'None'}</em>
            </SelectItem>
          )}
          {options.map(opt => {
            const optValue = opt.state?.[0] as string;
            return (
              <SelectItem key={optValue} value={optValue}>
                {opt.label || optValue}
              </SelectItem>
            );
          })}
        </SelectContent>
      </SelectRoot>

      {(error || description) && (
        <p
          id={`${name}-description`}
          className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}
        >
          {error || description}
        </p>
      )}
    </div>
  );
};
